import useSWR from 'swr'
import { AxiosResponse } from 'axios'
import { useMemo } from 'react'
import { APIDecoratorWithBaseURI } from '../../Service/api'

// Espelha o body de GET /eventComparisonTimeline (OP-3414) do backend analytics.
export interface ComparisonDay {
  dia: number
  ingressos: number
  ingressosPagos: number
  cortesias: number
  pedidos: number
  pedidosPagos: number
  receita: number
}

export interface ComparisonSeries {
  eventId: number // = groupIds[0] (compat)
  groupIds: number[]
  nome: string
  d0: string | null
  totalDias: number
  dias: ComparisonDay[]
  totais: {
    ingressos: number
    ingressosPagos: number
    cortesias: number
    pedidos: number
    receita: number
  }
}

export interface ComparisonBody {
  maxDay: number | null
  // Janela por minutos (OP-3517): quando setada, `dias` é uma série de baldes de
  // `bucketMinutes` minutos desde a 1ª venda paga (dia = índice do balde).
  maxMinutes?: number | null
  bucketMinutes?: number | null
  eventos: ComparisonSeries[]
}

/** 90 → "1h30" · 30 → "30min" · 120 → "2h". Rótulos das janelas por minuto. */
export const fmtMinutes = (m: number): string => {
  if (m < 60) return `${m}min`
  const h = Math.floor(m / 60)
  const r = m % 60
  return r ? `${h}h${String(r).padStart(2, '0')}` : `${h}h`
}

interface UseComparisonResult {
  comparison?: ComparisonBody
  comparisonLoading: boolean
  comparisonError: any
}

/**
 * Série diária alinhada por D+0 de 2+ GRUPOS de eventos (OP-3417). Cada grupo é um
 * "evento lógico" (vários eventos do painel somados). Envia `groups=csv;csv`.
 * `maxMinutes` (OP-3517) tem precedência sobre `maxDay` e pede a série em baldes
 * de minutos. Retorna `null` (desativa fetch) enquanto não houver 2 grupos não-vazios.
 */
export const useEventComparisonTimeline = (
  groups: number[][],
  maxDay?: number | null,
  maxMinutes?: number | null
): UseComparisonResult => {
  const clean = (groups || [])
    .map((g) => (g || []).map((v) => Number(v)).filter((n) => Number.isInteger(n) && n > 0))
    .filter((g) => g.length > 0)

  const windowQs =
    maxMinutes != null ? `&maxMinutes=${maxMinutes}` : maxDay != null ? `&maxDay=${maxDay}` : ''
  const url =
    clean.length >= 2
      ? `producer/analytics/eventComparisonTimeline?groups=${clean
          .map((g) => g.join(','))
          .join(';')}${windowQs}`
      : null

  const fetchData = async (): Promise<AxiosResponse> =>
    await APIDecoratorWithBaseURI().get(url as string)

  const { data, error, isLoading } = useSWR<any>(url, fetchData)

  const body: ComparisonBody | undefined = useMemo(
    () => data?.data?.body ?? data?.data?.data ?? undefined,
    [data]
  )

  return useMemo(
    () => ({ comparison: body, comparisonLoading: isLoading, comparisonError: error }),
    [body, isLoading, error]
  )
}

/* ───────────────────────── Público (Fase 2) ─────────────────────────
 * Reuso dos endpoints do OP-3411 (visitorsInformation, paymentMethods,
 * buyerRecurrence, salesLocator), todos filtrando por janela [startDate,endDate].
 * Aqui a janela é o CICLO DE VENDAS de CADA evento: [D0, D0+maxDay], com o D0
 * vindo do timeline. Sem SQL novo. */

const PA = 'producer/analytics/'
const paFetcher = (u: string): Promise<AxiosResponse> => APIDecoratorWithBaseURI().get(u)
const unwrap = (d: any) => d?.data?.body ?? d?.data?.data ?? undefined

const addDays = (iso: string, n: number): string => {
  const [y, m, d] = iso.split('-').map(Number)
  const dt = new Date(Date.UTC(y, m - 1, d))
  dt.setUTCDate(dt.getUTCDate() + n)
  return dt.toISOString().slice(0, 10)
}
const todayISO = (): string => new Date().toISOString().slice(0, 10)

// bandas de faixa etária alinhadas às do backend (visitorsInformation)
export const AGE_BANDS = ['0-17', '18-24', '25-34', '35-44', '45-54', '55+']
const AGE_MAP: Record<string, string> = {
  'até 17 anos': '0-17',
  'de 18 a 24 anos': '18-24',
  'de 25 a 34 anos': '25-34',
  'de 35 a 44 anos': '35-44',
  'de 45 a 54 anos': '45-54',
  'acima de 55 anos': '55+',
}
// agrupamento das categorias de pagamento (taxonomia OP-3400) p/ o gráfico
const PAY_GROUP: Record<string, string> = {
  PIX: 'PIX',
  CREDITO: 'Cartão',
  DEBITO: 'Cartão',
  NUPAY: 'NuPay',
  APPLE_PAY: 'Apple/Google Pay',
  GOOGLE_PAY: 'Apple/Google Pay',
  DINHEIRO: 'Dinheiro',
  CORTESIA: 'Cortesia',
  OUTRO: 'Outros',
}

export interface RecurrenceBucket {
  novosPct: number
  recorrentesPct: number
}
export interface SectorItem {
  id: number
  name: string
  qtd: number // ingressos (pagos + cortesias)
  pct: number // % do total de ingressos do evento
  unitPrice: number // ticket médio do setor (receita / pagantes)
}
export interface EventPublic {
  gender: { m: number; f: number; o: number } | null // % 0-100
  agesMap: Record<string, number> // banda -> %
  paymentsMap: Record<string, number> // grupo -> %
  statesMap: Record<string, number> // UF -> %
  citiesMap: Record<string, number> // cidade -> %
  sectors: SectorItem[] // setores do evento na janela, ordenados por volume
  recurrence: { comCortesia: RecurrenceBucket; semCortesia: RecurrenceBucket } | null
  loading: boolean
}

/**
 * Perfil do público de um GRUPO de eventos (evento lógico) na janela [D0, D0+maxDay].
 * Envia `eventIds` (CSV) — o backend agrega. Nº fixo de hooks (5 SWR).
 */
export const useEventPublic = (
  eventIds: number[],
  d0?: string | null,
  maxDay?: number | null
): EventPublic => {
  const ids = (eventIds || []).filter((n) => Number.isInteger(n) && n > 0)
  const start = ids.length && d0 ? d0 : null
  const end = start ? (maxDay != null ? addDays(start, maxDay) : todayISO()) : null
  // envia eventId (1º) + eventIds: o backend agrega por eventIds; o eventId satisfaz
  // a validação de parâmetro obrigatório desses endpoints (compat dashboard).
  const q = start ? `eventId=${ids[0]}&eventIds=${ids.join(',')}&startDate=${start}&endDate=${end}` : null

  const visitors = useSWR<any>(q ? `${PA}visitorsInformation?${q}` : null, paFetcher)
  const payments = useSWR<any>(q ? `${PA}paymentMethods?${q}` : null, paFetcher)
  const recur = useSWR<any>(q ? `${PA}buyerRecurrence?${q}` : null, paFetcher)
  const states = useSWR<any>(q ? `${PA}salesLocator?${q}` : null, paFetcher)
  const sectors = useSWR<any>(q ? `${PA}sectors/sales?${q}` : null, paFetcher)

  return useMemo<EventPublic>(() => {
    const vRaw = unwrap(visitors.data)
    const v = Array.isArray(vRaw) ? vRaw[0] : vRaw
    let gender: EventPublic['gender'] = null
    if (v) {
      const m = Number(v.Masculino) || 0
      const f = Number(v.Feminino) || 0
      const o = Number(v.Undefined) || 0
      const t = m + f + o
      gender = t > 0 ? { m: (m / t) * 100, f: (f / t) * 100, o: (o / t) * 100 } : null
    }
    const agesMap: Record<string, number> = {}
    ;(v?.Idades || []).forEach((it: any) => {
      const band = AGE_MAP[it.label]
      if (band) agesMap[band] = (agesMap[band] || 0) + (Number(it.value) || 0)
    })

    const pRaw = unwrap(payments.data)
    const paymentsMap: Record<string, number> = {}
    ;(Array.isArray(pRaw) ? pRaw : []).forEach((it: any) => {
      const g = PAY_GROUP[it.categoria] || 'Outros'
      paymentsMap[g] = (paymentsMap[g] || 0) + (Number(it.percentual) || 0)
    })

    const sRaw = unwrap(states.data)
    const statesArr = Array.isArray(sRaw) ? sRaw : []
    // % dos estados/cidades é sobre ingressos com UF conhecida: o balde
    // "Não informado" (uf==='') do salesLocator fica fora do denominador para
    // não diluir as barras (senão os estados nomeados não somariam ~100%).
    const totalStates = statesArr.reduce(
      (acc: number, it: any) =>
        acc + (String(it.uf || '').trim() ? Number(it.quantidadeIngressos) || 0 : 0),
      0
    )
    const statesMap: Record<string, number> = {}
    const citiesMap: Record<string, number> = {}
    statesArr.forEach((it: any) => {
      const uf = String(it.uf || '').toUpperCase()
      if (uf && totalStates > 0) {
        statesMap[uf] = ((Number(it.quantidadeIngressos) || 0) / totalStates) * 100
      }
      ;(it.cidades || []).forEach((c: any) => {
        const nome = String(c.nome || '').trim()
        if (nome && totalStates > 0) {
          citiesMap[nome] =
            (citiesMap[nome] || 0) + ((Number(c.quantidadeIngressos) || 0) / totalStates) * 100
        }
      })
    })

    // setores na janela (sectors/sales) — % sobre o total de ingressos do evento
    const secRaw = unwrap(sectors.data)
    const secArr = Array.isArray(secRaw) ? secRaw : []
    const secTotal = secArr.reduce((acc: number, it: any) => acc + (Number(it.public) || 0), 0)
    const sectorItems: SectorItem[] = secArr
      .map((it: any) => {
        const qtd = Number(it.public) || 0
        const payers = Number(it.payers) || 0
        return {
          id: Number(it.id) || 0,
          name: String(it.name ?? ''),
          qtd,
          pct: secTotal > 0 ? (qtd / secTotal) * 100 : 0,
          unitPrice: payers > 0 ? (Number(it.totalRevenue) || 0) / payers : 0,
        }
      })
      .filter((s: SectorItem) => s.qtd > 0)
      .sort((a: SectorItem, b: SectorItem) => b.qtd - a.qtd)

    const rRaw = unwrap(recur.data)
    const recurrence = rRaw
      ? {
          comCortesia: {
            novosPct: Number(rRaw.comCortesia?.percentualNovos) || 0,
            recorrentesPct: Number(rRaw.comCortesia?.percentualRecorrentes) || 0,
          },
          semCortesia: {
            novosPct: Number(rRaw.semCortesia?.percentualNovos) || 0,
            recorrentesPct: Number(rRaw.semCortesia?.percentualRecorrentes) || 0,
          },
        }
      : null

    const loading =
      visitors.isLoading ||
      payments.isLoading ||
      recur.isLoading ||
      states.isLoading ||
      sectors.isLoading

    return { gender, agesMap, paymentsMap, statesMap, citiesMap, sectors: sectorItems, recurrence, loading }
  }, [
    visitors.data,
    payments.data,
    recur.data,
    states.data,
    sectors.data,
    visitors.isLoading,
    payments.isLoading,
    recur.isLoading,
    states.isLoading,
    sectors.isLoading,
  ])
}

/* ── Quebras comerciais/comportamentais (OP-3415, endpoint eventCommercialBreakdown) ── */
export interface ChannelItem {
  key: string
  label: string
  qtd: number
  pct: number
}
export interface TicketTypeItem {
  tipo: string
  qtd: number
  pct: number
}
export interface LeadTimeItem {
  bucket: string
  label: string
  qtd: number
  pct: number
}
export interface CommercialHeatmap {
  bands: string[]
  days: string[]
  cells: number[][]
}
export interface EventCommercial {
  channels: ChannelItem[]
  ticketTypes: TicketTypeItem[]
  leadTime: LeadTimeItem[]
  heatmap: CommercialHeatmap | null
  loading: boolean
}

/** Canais, meia×inteira, antecedência e heatmap de um GRUPO na janela [D0, D0+maxDay]. */
export const useEventCommercial = (
  eventIds: number[],
  d0?: string | null,
  maxDay?: number | null
): EventCommercial => {
  const ids = (eventIds || []).filter((n) => Number.isInteger(n) && n > 0)
  const start = ids.length && d0 ? d0 : null
  const end = start ? (maxDay != null ? addDays(start, maxDay) : todayISO()) : null
  const q = start ? `eventId=${ids[0]}&eventIds=${ids.join(',')}&startDate=${start}&endDate=${end}` : null

  const { data, isLoading } = useSWR<any>(
    q ? `${PA}eventCommercialBreakdown?${q}` : null,
    paFetcher
  )

  return useMemo<EventCommercial>(() => {
    const b = unwrap(data)
    return {
      channels: Array.isArray(b?.channels) ? b.channels : [],
      ticketTypes: Array.isArray(b?.ticketTypes) ? b.ticketTypes : [],
      leadTime: Array.isArray(b?.leadTime) ? b.leadTime : [],
      heatmap: b?.heatmap ?? null,
      loading: isLoading,
    }
  }, [data, isLoading])
}

/* ── Origem das vendas por canal/campanha (OP-3419, endpoint campaignAttribution) ── */
export interface EventOrigin {
  data: any | undefined // espelha CampaignAttributionResponse do backend
  loading: boolean
}

/** Origem das vendas (GA4) de um GRUPO na janela [D0, D0+maxDay]. Reusa o endpoint OP-3419. */
export const useEventCampaignAttribution = (
  eventIds: number[],
  d0?: string | null,
  maxDay?: number | null
): EventOrigin => {
  const ids = (eventIds || []).filter((n) => Number.isInteger(n) && n > 0)
  const start = ids.length && d0 ? d0 : null
  const end = start ? (maxDay != null ? addDays(start, maxDay) : todayISO()) : null
  const q = start ? `eventId=${ids[0]}&eventIds=${ids.join(',')}&startDate=${start}&endDate=${end}` : null

  const { data, isLoading } = useSWR<any>(q ? `${PA}campaignAttribution?${q}` : null, paFetcher)

  return useMemo<EventOrigin>(() => ({ data: unwrap(data), loading: isLoading }), [data, isLoading])
}

/* ── Recorrência cruzada entre os dois lados (clientes que compraram AMBOS) ── */
export interface CrossRecurrenceSide {
  clientes: number
  clientesPagantes: number
}
export interface CrossRecurrence {
  base?: CrossRecurrenceSide
  compare?: CrossRecurrenceSide
  overlap?: CrossRecurrenceSide
  loading: boolean
}

/**
 * Interseção de clientes (por CPF) entre o grupo base e o grupo comparado —
 * quantos compraram os DOIS eventos. Sem janela: histórico completo de cada
 * evento. Só dispara quando ambos os lados têm evento selecionado.
 */
export const useEventCrossRecurrence = (
  baseIds: number[],
  compareIds: number[]
): CrossRecurrence => {
  const bIds = (baseIds || []).filter((n) => Number.isInteger(n) && n > 0)
  const cIds = (compareIds || []).filter((n) => Number.isInteger(n) && n > 0)
  const q =
    bIds.length && cIds.length
      ? `baseIds=${bIds.join(',')}&compareIds=${cIds.join(',')}`
      : null

  const { data, isLoading } = useSWR<any>(q ? `${PA}eventCrossRecurrence?${q}` : null, paFetcher)

  return useMemo<CrossRecurrence>(() => {
    const body = unwrap(data) || {}
    return {
      base: body.base,
      compare: body.compare,
      overlap: body.overlap,
      loading: isLoading,
    }
  }, [data, isLoading])
}

/* ── Mapeamento de setores para categorias canônicas (OP-3415 Fase B) ── */
export interface SectorCategory {
  key: string
  label: string
  color: string
}
export interface MappingSector {
  id: number
  eventId: number
  name: string
  qtd: number
  unitPrice: number
  category: string
  suggested: boolean
}
export interface SectorMappingData {
  categories: SectorCategory[]
  sectors: MappingSector[]
  hasSaved: boolean
}

/** Mapeamento de setores de um GRUPO de eventos (envia `eventIds` CSV). */
export const useSectorMapping = (eventIds: number[]) => {
  const ids = (eventIds || []).filter((n) => Number.isInteger(n) && n > 0)
  const url = ids.length ? `${PA}sectorMapping?eventId=${ids[0]}&eventIds=${ids.join(',')}` : null
  const { data, isLoading, mutate } = useSWR<any>(url, paFetcher)
  const mapping = useMemo<SectorMappingData | undefined>(() => unwrap(data), [data])
  return { mapping, loading: isLoading, refetch: mutate }
}

/** PUT do mapa { setorId: categoria } de um GRUPO (o backend divide por evento). */
export const saveSectorMapping = (eventIds: number[], mapping: Record<string, string>) =>
  APIDecoratorWithBaseURI().put(
    `${PA}sectorMapping?eventId=${eventIds[0]}&eventIds=${eventIds.join(',')}`,
    { mapping }
  )

export const IGNORE_KEY = 'ignorar'
const CATEGORY_PALETTE = [
  '#3456DA', '#12B76A', '#F79009', '#7A5AF8', '#EF6820', '#0BA5EC',
  '#EE46BC', '#F63D68', '#16B364', '#EAAA08', '#6172F3', '#DD2590',
]
/** Cor determinística por rótulo — espelha colorForLabel do backend. */
export const categoryColor = (label: string): string => {
  if (label === IGNORE_KEY) return '#8A96AD'
  let h = 0
  for (let i = 0; i < label.length; i++) h = (h * 31 + label.charCodeAt(i)) >>> 0
  return CATEGORY_PALETTE[h % CATEGORY_PALETTE.length]
}
